import React from 'react'
import styled from 'styled-components/native'
import { Linking, ImageSourcePropType } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import Typography from 'APP/Converse/Typography'
import Button from 'APP/Converse/Button'
import I18n from 'APP/I18n'
import { ServiceGroupImage } from 'APP/Components/ServiceGroupImage'
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import { logger } from 'APP/Lib/Datadog'

interface Props {
  headerText?: string | null
  subHeaderText?: string | null
  returnButtonText?: string | null
  screenIconOverride?: ImageSourcePropType
  serviceGroup?: Record<string, unknown>
  onRetry: () => void
  onReturn?: () => void
}

const Container = styled.View`
  flex: 1;
  align-items: center;
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const ContentWrapper = styled.View`
  align-items: flex-start;
`

const MainHeader = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 0.5}px;
`

const SubHeader = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  color: ${({ theme }) => theme.colors.text};
`

const Content = styled.View`
  align-items: center;
`

const Wrapper = styled.View`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 1.25}px;
  width: 311px;
`

const StyledButton = styled(Button)`
  margin-right: ${({ theme }) => theme.metrics.baseMargin * 0.5}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
`

const ContactUsContainer = styled.View`
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
  align-items: center;
`

const ContactUsLink = styled(Typography)`
  color: ${({ theme }) => theme.colors.text};
`

const ServiceAuthenticationError = ({
  headerText,
  subHeaderText,
  returnButtonText,
  screenIconOverride,
  serviceGroup,
  onRetry,
  onReturn,
}: Props) => {
  const navigation = useNavigation()

  const resolvedHeaderText =
    headerText ?? I18n.t('ServiceAuthenticationError.failedRetryError.header') ?? ''
  const resolvedSubHeaderText =
    subHeaderText ?? I18n.t('ServiceAuthenticationError.failedRetryError.subHeader') ?? ''
  const resolvedReturnButtonText =
    returnButtonText ?? I18n.t('ServiceAuthenticationError.failedRetryError.returnButton') ?? ''
  const handleReturn = onReturn ?? (() => navigation.goBack())

  const handleEmailPress = () => {
    const url = UrlBuilder.supportEmailUrl()
    Linking.openURL(url).catch((err) => {
      logger.error('Error opening email', {}, err)
    })
  }

  return (
    <Container testID="SERVICE_AUTHENTICATION_ERROR">
      <ContentWrapper>
        <ServiceGroupImage
          source={screenIconOverride ?? {}}
          serviceGroup={serviceGroup ?? {}}
          testID="SERVICE_GROUP_IMAGE"
        />
        <MainHeader variant="h1">{resolvedHeaderText}</MainHeader>
        <SubHeader variant="subheader">{resolvedSubHeaderText}</SubHeader>
      </ContentWrapper>

      <Content>
        <Wrapper>
          <StyledButton
            variant="primary"
            title={I18n.t('ServiceAuthenticationError.failedRetryError.retryButton') ?? ''}
            widthVariant="full"
            testID="RETRY_BUTTON"
            onPress={onRetry}
          />
          <StyledButton
            variant="secondary"
            title={resolvedReturnButtonText}
            widthVariant="full"
            testID="GO_BACK_BUTTON"
            onPress={handleReturn}
          />
        </Wrapper>
        <ContactUsContainer>
          <Typography variant="subheader">
            {I18n.t('ServiceAuthenticationError.failedRetryError.contactUs')}{' '}
            <ContactUsLink variant="link" onPress={handleEmailPress}>
              {I18n.t('ServiceAuthenticationError.failedRetryError.sendEmail')}
            </ContactUsLink>
          </Typography>
        </ContactUsContainer>
      </Content>
    </Container>
  )
}

export default ServiceAuthenticationError
